import React, { useContext, useRef, useState } from "react"
import tw from "twin.macro"
import { ReactComponent as Diarra } from "../../assets/homepage/diarra.svg"
import { ReactComponent as Unmute } from "../../assets/homepage/unmute.svg"
import { ReactComponent as Mute } from "../../assets/homepage/mute.svg"
import { ReactComponent as Reazy } from "../../assets/homepage/reazy.svg"
import { useTranslation } from "next-i18next"
import { Button } from "../formElements/button"
import SourceAnimation from "../../assets/homepage/source.json"
import CreateAnimation from "../../assets/homepage/create.json"
import DistributeAnimation from "../../assets/homepage/distribute.json"
import StarburstAnimation from "../../assets/homepage/starburst.json"
import { LottieAnimation } from "../lottieAnimation"
import { isMobile } from "react-device-detect"
import { Testimonies } from "./testimonies"
import { AuthContext } from "../../utils/context"
import { AnimationData } from "react-lottie-hook"
import { Authentication, AuthenticatonPage } from "../../components/authentication"
import { Modal } from "../../components/modal"
import { ReactComponent as DiscordBtnIcon } from "../../assets/discord/discord-white.svg"
import { getDiscordInstallationUrl } from "../../utils/discord"
import Link from "next/link"

export const Homepage = () => {
  const { token } = useContext(AuthContext)
  const { t } = useTranslation("homepage")
  const [isMuted, setIsMuted] = useState(true)
  const [view, setView] = useState(AuthenticatonPage.Join)
  const [show, setShow] = useState(false)
  const [isNotDismissable, setIsNotDismissable] = useState(false)
  const lottieWidth = isMobile ? 328 : 544
  const lottieHeight = isMobile ? 334 : 554
  const vidRef = useRef<HTMLVideoElement>(null)
  const modalProps = {
    show,
    setShow,
    notDismissable: isNotDismissable,
    component: <Authentication view={view} setShow={setShow} setNotDismissable={setIsNotDismissable} />,
  }
  return (
    <div tw="bg-base-6 text-white pt-5 tablet-lg:space-y-10">
      <section tw="flex items-center justify-center flex-wrap tablet-lg:flex-nowrap">
        <Diarra tw="h-auto tablet-lg:h-[823px]" />
        <div tw="w-full py-10 px-5 text-center tablet-lg:(w-1/2 text-left)">
          <div tw="w-1/2 mx-auto py-4 items-center tablet-lg:(w-60 mx-0)">
            <Link prefetch={false} passHref={true} href={getDiscordInstallationUrl()}>
              <a>
                <Button icon={<DiscordBtnIcon />} twStyle={tw`w-full p-3`} action={`${t("install_discord")}`} />
              </a>
            </Link>
          </div>
          <h1 tw="text-2xl font-bold tablet-lg:text-4xl">{t("fans_ask")}</h1>
          <h2 tw="text-2xl font-bold tablet-lg:text-4xl pb-10">{t("we_handle")}</h2>
        </div>
      </section>
      <section tw="flex items-center justify-center flex-wrap space-y-5 tablet-lg:(flex-nowrap space-x-28)">
        <div tw="w-full py-10 px-5 space-y-5 order-2 tablet-lg:w-2/5">
          <h1 tw="text-2xl font-bold tablet-lg:text-4xl">{t("say_hello")}</h1>
          <p tw="text-lg font-bold tablet-lg:text-2xl">{t("platform_for_artists")}</p>
        </div>
        <div tw="relative w-[297px] h-[528px] tablet-lg:(order-2 w-[393px] h-[698px])">
          <video
            id="vid"
            controls={false}
            loop
            autoPlay
            muted={isMuted}
            src="https://bonsai-media.s3.amazonaws.com/ColliecutHomepage.mp4"
            preload="metadata"
            ref={vidRef}
            playsInline
          >
            <source src="https://bonsai-media.s3.amazonaws.com/ColliecutHomepage.mp4" type="video/mp4" />
          </video>
          <span tw="m-5 absolute left-0 bottom-[20px] cursor-pointer" onClick={() => setIsMuted(!isMuted)}>
            {isMuted ? <Mute /> : <Unmute />}
          </span>
        </div>
      </section>
      <section tw="flex items-center justify-center flex-wrap space-y-5 tablet-lg:(flex-nowrap space-x-16)">
        <LottieAnimation width={lottieWidth} height={lottieHeight} animationData={SourceAnimation as AnimationData} />
        <div tw="w-full py-10 px-5 space-y-5 tablet-lg:w-2/6">
          <h1 tw="text-2xl font-bold tablet-lg:text-3xl">{t("meet_your_audience")}</h1>
          <p tw="text-lg">{t("showing_for_supperfans")}</p>
        </div>
      </section>
      <section tw="relative flex items-center justify-center flex-wrap space-y-5 tablet-lg:(flex-nowrap space-x-16 pt-20)">
        <LottieAnimation
          twStyle={tw`hidden tablet-lg:(inline absolute right-0 top-[-110px] z-10)`}
          width={isMobile ? 144 : 212}
          height={isMobile ? 144 : 212}
          animationData={StarburstAnimation as AnimationData}
        />
        <div tw="w-full py-10 px-5 space-y-5 order-2 tablet-lg:w-2/6">
          <h1 tw="text-2xl font-bold tablet-lg:text-3xl">{t("create_collaborations")}</h1>
          <p tw="text-lg">{t("supperfans_the_chance")}</p>
        </div>
        <LottieAnimation
          twStyle={tw`tablet-lg:order-2`}
          width={lottieWidth}
          height={lottieHeight}
          animationData={CreateAnimation as AnimationData}
        />
      </section>
      <section tw="flex items-center justify-center flex-wrap space-y-5 tablet-lg:(flex-nowrap space-x-16)">
        <LottieAnimation
          width={lottieWidth}
          height={lottieHeight}
          animationData={DistributeAnimation as AnimationData}
        />
        <div tw="w-full py-10 px-5 space-y-5 tablet-lg:w-2/6">
          <h1 tw="text-2xl font-bold tablet-lg:text-3xl">{t("heavy_lifting")}</h1>
          <p tw="text-lg">{t("we_post")}</p>
        </div>
      </section>
      <Testimonies />
      <section tw="flex items-center justify-end flex-wrap tablet-lg:(flex-nowrap)">
        <div tw="self-center w-full py-10 px-5 space-y-5 tablet-lg:(w-1/2 space-y-10)">
          <h1 tw="text-2xl font-bold tablet-lg:text-4xl">{t("join_forces")}</h1>
          <p tw="text-lg font-bold tablet-lg:text-2xl">{t("start_creating")}</p>
          {!token && (
            <Button
              action={t("sign_up")}
              onClick={() => {
                setView(AuthenticatonPage.Join)
                setShow(true)
              }}
            />
          )}
        </div>
        <Reazy tw="h-auto" />
      </section>
      <Modal {...modalProps} />
    </div>
  )
}
